import Tooltip from "@/components/Tooltip";
import { UseCase } from "@/services/global-services/models";
import { useDataStore, useFilterStore } from "@/stores";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrianglePersonDigging } from "@fortawesome/sharp-light-svg-icons";
import { ChevronDownIcon, MinusIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { MouseEvent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

interface UseCaseBlockProps {
    id: string;
    useCase: UseCase;
    selected?: boolean;
    expanded?: boolean;
    small?: boolean;

    onClick: (id: string) => void;
    onSelect: (id: string) => void;
}

const UseCaseBlock = ({
    id,
    useCase,
    selected = false,
    expanded = false,
    small = false,
    onClick,
    onSelect,
}: UseCaseBlockProps) => {
    const { t } = useTranslation(["filters"]);
    const { locale } = useParams();
    const pages = useDataStore((state) => state.pages);
    const filtersModalOpen = useFilterStore((state) => state.open);
    const activeDisciplines = useFilterStore((state) => state.activeDisciplines);
    const unconfirmedDisciplines = useFilterStore((state) => state.unconfirmedDisciplines);

    const disciplines = useMemo(
        () => (filtersModalOpen ? unconfirmedDisciplines : activeDisciplines) ?? [],
        [filtersModalOpen, activeDisciplines, unconfirmedDisciplines]
    );

    const slug = pages.find((p) => p.id === useCase.internalLink)?.url;

    const handleExpandToggle = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onClick(useCase.id);
    };

    const handleSelectToggle = (e: MouseEvent<HTMLInputElement>) => {
        e.stopPropagation();
        onSelect(useCase.id);
    };

    const attachedToDispline = useMemo(() => {
        return (
            !useCase.attachedDiscipline ||
            useCase.attachedDiscipline.some((discipline) => disciplines.includes(discipline))
        );
    }, [useCase, disciplines]);

    return (
        <div
            className={clsx([
                "group rounded-lg gap-4 cursor-pointer",
                small ? "min-h-14 text-base p-3.5" : "p-5",
                selected && attachedToDispline ? "bg-primary-500 text-white" : "bg-primary-100 text-primary-500",
                !attachedToDispline && "opacity-30 pointer-events-none",
            ])}
            onClick={handleSelectToggle}
        >
            <div className="flex items-center justify-between space-x-2">
                <div className="flex h-7 items-center space-x-3 select-none cursor-pointer mr-2">
                    <div className="group grid size-6 grid-cols-1 shrink-0">
                        <input
                            id={id}
                            name={id}
                            type="checkbox"
                            checked={selected && attachedToDispline}
                            onClick={handleSelectToggle}
                            onChange={() => {}}
                            className={clsx([
                                "col-start-1 row-start-1 appearance-none rounded-lg border-2 min-w-6 min-h-6 cursor-pointer",
                                "border-primary-300 bg-primary-100",
                                "checked:bg-white checked:outline-2 checked:-outline-offset-4 checked:outline-primary-500",
                                "indeterminate:bg-indigo-600",
                                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
                                "disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto",
                            ])}
                        />
                        <svg
                            fill="none"
                            viewBox="0 0 14 14"
                            className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-primary-500 group-has-disabled:stroke-gray-950/25"
                        >
                            <path
                                d="M3 8L6 11L11 3.5"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="opacity-0 group-has-checked:opacity-100"
                            />
                            <path
                                d="M3 7H11"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="opacity-0 group-has-indeterminate:opacity-100"
                            />
                        </svg>
                    </div>
                    <p className="font-medium whitespace-break-spaces">{useCase.name ?? useCase.code}</p>
                </div>

                <div className="ml-auto flex items-center space-x-2">
                    {!useCase.isComplete && (
                        <Tooltip text={t("usecases.incomplete-tooltip", { ns: "filters" })}>
                            <FontAwesomeIcon icon={faTrianglePersonDigging} size="xl" className="ml-auto" />
                        </Tooltip>
                    )}

                    <button onClick={handleExpandToggle}>
                        {expanded ? (
                            <MinusIcon
                                className={clsx([
                                    "w-5 h-5",
                                    selected
                                        ? "text-white hover:text-primary-100"
                                        : "text-primary-500 hover:text-primary-600",
                                    "focus:outline-none cursor-pointer",
                                ])}
                            />
                        ) : (
                            <ChevronDownIcon
                                className={clsx([
                                    "w-5 h-5",
                                    selected
                                        ? "text-white hover:text-primary-100"
                                        : "text-primary-500 hover:text-primary-600",
                                    "focus:outline-none cursor-pointer",
                                ])}
                            />
                        )}
                    </button>
                </div>
            </div>

            <div
                aria-expanded={expanded}
                className={clsx([
                    "overflow-hidden",
                    "transition-transform duration-300 ease-in-out",
                    expanded ? "h-fit" : "h-0",
                ])}
            >
                <p className="text-sm/[20px] font-medium mt-4 ml-8">
                    {useCase.description}
                    {slug && (
                        <>
                            <Link
                                to={`/${locale}/${slug}`}
                                className="float-right ml-4 text-primary-800 underline group-has-checked:text-primary-100 hover:text-primary-500 group-has-checked:hover:text-primary-300 whitespace-nowrap"
                                onClick={(e) => e.stopPropagation()}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t("usecases.read-more", { ns: "filters" })}
                            </Link>
                        </>
                    )}
                </p>
            </div>
        </div>
    );
};

export default UseCaseBlock;
